import { usePreset, updatePrimaryPalette, updateSurfacePalette, definePreset } from "@primeuix/themes";
import Lara from "@primeuix/themes/lara-compat";
import Aura from "@primeuix/themes/aura-compat";
import Material from "@primeuix/themes/material-compat";

const THEME_STORAGE_KEY = "epicai-theme-id";
const COLOR_STORAGE_KEY = "epicai-color-id";

const THEME_FONTS = {
  saga: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
  lara: '"Inter var", sans-serif',
  aura: '"Inter var", sans-serif',
  "md-light":
    'Roboto, "Helvetica Neue Light", "Helvetica Neue", Helvetica, Arial, "Lucida Grande", sans-serif',
};

function presetWithFont(basePreset, fontFamily) {
  return definePreset(basePreset, {
    semantic: {
      typography: {
        fontFamily,
      },
    },
  });
}

const PRESETS = {
  saga: presetWithFont(Lara, THEME_FONTS.saga),
  lara: presetWithFont(Lara, THEME_FONTS.lara),
  aura: presetWithFont(Aura, THEME_FONTS.aura),
  mdLight: presetWithFont(Material, THEME_FONTS["md-light"]),
};

const DEFAULT_PRESET = PRESETS.saga;

const THEME_FAMILIES = {
  1: {
    name: "saga",
    preset: PRESETS.saga,
    colors: {
      0: { name: "Default", palette: "orange" },
      1: { name: "Green", palette: "green" },
      2: { name: "Blue", palette: "blue" },
      3: { name: "Purple", palette: "purple" },
    },
  },
  2: {
    name: "lara",
    preset: PRESETS.lara,
    colors: {
      0: { name: "Default", palette: "blue" },
      1: { name: "Indigo", palette: "indigo" },
      2: { name: "Green", palette: "green" },
      3: { name: "Teal", palette: "teal" },
      4: { name: "Cyan", palette: "cyan" },
      5: { name: "Amber", palette: "amber" },
      6: { name: "Pink", palette: "pink" },
      7: { name: "Purple", palette: "purple" },
    },
  },
  3: {
    name: "aura",
    preset: PRESETS.aura,
    colors: {
      0: { name: "Default", palette: "lime" },
      1: { name: "Blue", palette: "blue" },
      2: { name: "Green", palette: "green" },
      3: { name: "Orange", palette: "orange" },
      4: { name: "Pink", palette: "pink" },
      5: { name: "Purple", palette: "purple" },
    },
  },
  4: {
    name: "md-light",
    preset: PRESETS.mdLight,
    colors: {
      0: { name: "Default", palette: "indigo" },
      1: { name: "Deep Purple", palette: "purple" },
    },
  },
};

const THEME_FAMILY_COLORS = {};
Object.keys(THEME_FAMILIES).forEach((themeId) => {
  THEME_FAMILY_COLORS[themeId] = Object.keys(THEME_FAMILIES[themeId].colors).map(
    (id) => ({
      id,
      name: THEME_FAMILIES[themeId].colors[id].name,
    }),
  );
});

export const THEME_OPTIONS = {
  darkModeSelector: "none",
  cssLayer: {
    name: "primevue",
    order: "tailwind-base, primevue, tailwind-utilities",
  },
};

const LIGHT_SURFACE_PALETTE = {
  0: "#ffffff",
  50: "{slate.50}",
  100: "{slate.100}",
  200: "{slate.200}",
  300: "{slate.300}",
  400: "{slate.400}",
  500: "{slate.500}",
  600: "{slate.600}",
  700: "{slate.700}",
  800: "{slate.800}",
  900: "{slate.900}",
  950: "{slate.950}",
};

function ensureLightSurfaces() {
  updateSurfacePalette({ light: LIGHT_SURFACE_PALETTE });
}

const PRIMARY_STEPS = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

// palette(name) returns the palette name for every step (e.g. "orange"), which
// makes --p-primary-100 == --p-primary-color. Token refs resolve to real shades.
function primaryPaletteTokens(paletteName) {
  return Object.fromEntries(
    PRIMARY_STEPS.map((step) => [step, `{${paletteName}.${step}}`]),
  );
}

const RADIO_OVERRIDES = `
.p-radiobutton { width: 1.25rem !important; height: 1.25rem !important; }
.p-radiobutton .p-radiobutton-input { border-width: 1px !important; }
.p-radiobutton .p-radiobutton-box {
  width: 1.25rem !important;
  height: 1.25rem !important;
  background: #ffffff !important;
  border: 1px solid #ced4da !important;
  border-radius: 50% !important;
  box-shadow: none !important;
}
.p-radiobutton .p-radiobutton-box .p-radiobutton-icon {
  width: 0.75rem !important;
  height: 0.75rem !important;
  border-radius: 50%;
  transform: translateZ(0) scale(0.1);
  visibility: hidden;
  background-color: transparent !important;
}
.p-radiobutton:not(.p-radiobutton-checked):not(.p-disabled):has(.p-radiobutton-input:hover) .p-radiobutton-box {
  background: #ffffff !important;
  border-color: #ced4da !important;
}
.p-radiobutton.p-radiobutton-checked .p-radiobutton-box {
  background: #ffffff !important;
  border-color: var(--p-primary-color) !important;
}
.p-radiobutton.p-radiobutton-checked .p-radiobutton-box .p-radiobutton-icon {
  background-color: var(--p-primary-color) !important;
  transform: translateZ(0) scale(1, 1) !important;
  visibility: visible !important;
}
.p-radiobutton:not(.p-disabled):has(.p-radiobutton-input:hover).p-radiobutton-checked .p-radiobutton-box {
  border-color: var(--p-primary-color) !important;
  background: #ffffff !important;
}
.p-radiobutton:not(.p-disabled):has(.p-radiobutton-input:hover).p-radiobutton-checked .p-radiobutton-box .p-radiobutton-icon {
  background-color: var(--p-primary-color) !important;
}
`;

function ensureRadioOverrides() {
  const styleId = "app-theme-overrides";
  let style = document.getElementById(styleId);
  if (!style) {
    style = document.createElement("style");
    style.id = styleId;
    document.head.appendChild(style);
  }
  style.textContent = RADIO_OVERRIDES;
}

function getDefaultThemeSelection() {
  return { themeId: 1, colorId: "0" };
}

export function getSavedTheme() {
  try {
    const themeId = localStorage.getItem(THEME_STORAGE_KEY);
    if (themeId == null || !THEME_FAMILIES[themeId]) return null;
    let colorId = localStorage.getItem(COLOR_STORAGE_KEY);
    if (colorId == null || !THEME_FAMILIES[themeId].colors[colorId]) {
      colorId = "0";
    }
    return { themeId: Number(themeId), colorId };
  } catch (e) {
    return null;
  }
}

export function saveColorScheme(themeId, colorId) {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, String(themeId));
    localStorage.setItem(COLOR_STORAGE_KEY, String(colorId));
  } catch (e) {}
}

export function getColorsForTheme(themeId) {
  return THEME_FAMILY_COLORS[themeId] || [];
}

export function getInitialPreset() {
  const saved = getSavedTheme() || getDefaultThemeSelection();
  const family = THEME_FAMILIES[saved.themeId] || THEME_FAMILIES[1];
  return family.preset || DEFAULT_PRESET;
}

export function applyColorScheme(themeId, colorId) {
  const family = THEME_FAMILIES[themeId];
  if (!family) return;
  const color = family.colors[colorId] || family.colors["0"];
  usePreset(family.preset);
  ensureLightSurfaces();
  if (color?.palette) {
    updatePrimaryPalette(primaryPaletteTokens(color.palette));
  }
  ensureRadioOverrides();
}

export function applySavedColorScheme() {
  const saved = getSavedTheme();
  if (saved) {
    applyColorScheme(saved.themeId, saved.colorId);
  } else {
    const defaults = getDefaultThemeSelection();
    applyColorScheme(defaults.themeId, defaults.colorId);
  }
}

export const themeApi = {
  getSavedTheme,
  saveColorScheme,
  getColorsForTheme,
  applyColorScheme,
};
